import type { PrismaClient } from '@prisma/client';
import type { Request, Response, Router } from 'express';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import { permissionsGranted } from '../auth/auth.middleware.js';
import { renderDocumentPdf } from '../sales/pdf.service.js';
import {
  assertCanSend,
  sendDocument,
  sendInputSchema,
  sendPreview,
} from '../sales/send.service.js';
import { createShareLink, shareUrl } from '../sales/share.service.js';
import { loadOrder } from './purchases.service.js';

/** PDF, secure link and Send dialog endpoints for purchase orders and payout receipts. */
export function mountPurchaseSendRoutes(
  router: Router,
  { db, config }: { db: PrismaClient; config: Config },
) {
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const sendPdf = (req: Request, res: Response, pdf: Buffer, filename: string) => {
    res.setHeader('Content-Type', 'application/pdf');
    res.setHeader('X-Content-Type-Options', 'nosniff');
    res.setHeader(
      'Content-Disposition',
      `${req.query.inline === '1' ? 'inline' : 'attachment'}; filename="${filename.replace(/"/g, '')}"`,
    );
    res.send(pdf);
  };
  const orderFor = async (req: Request, action: 'view' | 'send') => {
    if (!can(req, `purchases.order.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    return loadOrder(db, req.auth!, Number(req.params.id));
  };
  const payoutFor = async (req: Request, action: 'view' | 'send') => {
    if (!can(req, `purchases.payout.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    const p = await db.payoutReceipt.findFirst({
      where: {
        id: Number(req.params.id),
        companyId: req.auth!.companyId,
        ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
      },
    });
    if (!p) throw new HttpError(404, 'NOT_FOUND', 'Payout not found');
    return p;
  };

  router.get(
    '/purchases/orders/:id/pdf',
    asyncHandler(async (req, res) => {
      const o = await orderFor(req, 'view');
      const { pdf, filename } = await renderDocumentPdf(db, o.companyId, 'purchaseorder', o.id);
      sendPdf(req, res, pdf, filename);
    }),
  );
  router.get(
    '/purchases/payouts/:id/pdf',
    asyncHandler(async (req, res) => {
      const p = await payoutFor(req, 'view');
      const { pdf, filename } = await renderDocumentPdf(db, p.companyId, 'payout', p.id);
      sendPdf(req, res, pdf, filename);
    }),
  );

  router.post(
    '/purchases/orders/:id/share-link',
    asyncHandler(async (req, res) => {
      const o = await orderFor(req, 'send');
      if (['draft', 'pending_approval'].includes(o.status))
        throw new HttpError(409, 'NEEDS_APPROVAL', 'Approve the purchase order before sharing it');
      const { token, expiresAt } = await createShareLink(db, {
        companyId: o.companyId,
        entityType: 'purchase_order',
        entityId: o.id,
        userId: req.auth!.userId,
      });
      await audit(db, actor(req), {
        action: 'share_link.created',
        entity: 'purchase_order',
        entityId: o.id,
      });
      res.status(201).json({ url: shareUrl(config.APP_URL, token), expiresAt });
    }),
  );
  router.post(
    '/purchases/payouts/:id/share-link',
    asyncHandler(async (req, res) => {
      const p = await payoutFor(req, 'send');
      if (p.status === 'void')
        throw new HttpError(409, 'INVALID_STATE', 'A void payout cannot be shared');
      const { token, expiresAt } = await createShareLink(db, {
        companyId: p.companyId,
        entityType: 'payout',
        entityId: p.id,
        userId: req.auth!.userId,
      });
      res.status(201).json({ url: shareUrl(config.APP_URL, token), expiresAt });
    }),
  );

  router.get(
    '/purchases/orders/:id/send-preview',
    asyncHandler(async (req, res) => {
      const o = await orderFor(req, 'send');
      res.json(await sendPreview(db, req.auth!, 'purchaseorder', o.id));
    }),
  );
  router.get(
    '/purchases/payouts/:id/send-preview',
    asyncHandler(async (req, res) => {
      const p = await payoutFor(req, 'send');
      res.json(await sendPreview(db, req.auth!, 'payout', p.id));
    }),
  );
  router.post(
    '/purchases/orders/:id/send',
    asyncHandler(async (req, res) => {
      const o = await orderFor(req, 'send');
      assertCanSend(req.auth!, 'purchaseorder');
      const body = parse(sendInputSchema, req.body);
      const result = await sendDocument(db, config, req.auth!, 'purchaseorder', o.id, body);
      await audit(db, actor(req), {
        action: 'purchase_order.sent',
        entity: 'purchase_order',
        entityId: o.id,
        after: { channels: body.channels, recipients: result.communications.length },
      });
      res.status(201).json(result);
    }),
  );
  router.post(
    '/purchases/payouts/:id/send',
    asyncHandler(async (req, res) => {
      const p = await payoutFor(req, 'send');
      const body = parse(sendInputSchema, req.body);
      const result = await sendDocument(db, config, req.auth!, 'payout', p.id, body);
      await audit(db, actor(req), {
        action: 'payout.sent',
        entity: 'payout',
        entityId: p.id,
        after: { channels: body.channels },
      });
      res.status(201).json(result);
    }),
  );
}
